// RFC 6238 TOTP (HMAC-SHA1, 6 digits, 30 s step) — for showing 2FA codes stored
// in items, and for setting up the user's own sign-in authenticator.

import { randomBytes, type Bytes } from "./encoding";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
export const TOTP_STEP = 30;

export function base32Encode(bytes: Uint8Array): string {
  let bits = 0;
  let value = 0;
  let out = "";
  for (const b of bytes) {
    value = (value << 8) | b;
    bits += 8;
    while (bits >= 5) {
      out += ALPHABET[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) out += ALPHABET[(value << (5 - bits)) & 31];
  return out;
}

export function base32Decode(input: string): Bytes {
  // Accept otpauth:// URIs and keys pasted with spaces or lower case.
  const fromUri = /secret=([^&]+)/i.exec(input);
  const clean = (fromUri ? decodeURIComponent(fromUri[1]!) : input)
    .toUpperCase()
    .replace(/[^A-Z2-7]/g, "");
  let bits = 0;
  let value = 0;
  const out: number[] = [];
  for (const ch of clean) {
    value = (value << 5) | ALPHABET.indexOf(ch);
    bits += 5;
    if (bits >= 8) {
      out.push((value >>> (bits - 8)) & 0xff);
      bits -= 8;
    }
  }
  return new Uint8Array(out);
}

/** A fresh 160-bit secret, base32 — the size authenticator apps expect. */
export const newTotpSecret = (): string => base32Encode(randomBytes(20));

export async function hotp(key: Bytes, counter: number): Promise<string> {
  const k = await crypto.subtle.importKey(
    "raw",
    key,
    { name: "HMAC", hash: "SHA-1" },
    false,
    ["sign"],
  );
  const msg = new ArrayBuffer(8);
  const view = new DataView(msg);
  view.setUint32(0, Math.floor(counter / 2 ** 32));
  view.setUint32(4, counter >>> 0);
  const mac = new Uint8Array(await crypto.subtle.sign("HMAC", k, msg));
  const offset = mac[mac.length - 1]! & 0x0f;
  const bin =
    ((mac[offset]! & 0x7f) << 24) |
    (mac[offset + 1]! << 16) |
    (mac[offset + 2]! << 8) |
    mac[offset + 3]!;
  return (bin % 1_000_000).toString().padStart(6, "0");
}

/** The current code for a base32 secret, or null if the secret is unusable. */
export async function totpNow(
  secret: string,
  nowMs = Date.now(),
): Promise<string | null> {
  const key = base32Decode(secret);
  if (key.length < 10) return null;
  return hotp(key, Math.floor(nowMs / 1000 / TOTP_STEP));
}

export function otpauthUri(secret: string, account: string): string {
  const label = encodeURIComponent(`Agile Vault:${account}`);
  return `otpauth://totp/${label}?secret=${secret}&issuer=Agile%20Vault&algorithm=SHA1&digits=6&period=30`;
}

/** Ten one-use backup codes, 0000-0000, from a uniform random source. */
export function newBackupCodes(): string[] {
  const out: string[] = [];
  const buf = new Uint32Array(1);
  while (out.length < 10) {
    crypto.getRandomValues(buf);
    // Rejection-sample to avoid modulo bias over 10^8.
    if (buf[0]! >= 4_200_000_000) continue;
    const s = (buf[0]! % 100_000_000).toString().padStart(8, "0");
    out.push(`${s.slice(0, 4)}-${s.slice(4)}`);
  }
  return out;
}
